import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { BRANCHES } from '@/mocks/mockData';

interface GrupDers {
  id: string;
  grup_adi: string;
  brans: string;
  seviye: string;
  ogretmen_id: string | null;
  maksimum_kontenjan: number | null;
  ders_gunleri: string[] | null;
  ders_saati: string | null;
  grup_ders_ucreti: number | null;
  baslangic_tarihi: string | null;
  aciklama: string | null;
  durum: string | null;
  ogretmen?: { ad_soyad: string };
  kayit_sayisi?: number;
}

const statusColors: Record<string, string> = {
  'Talep Toplama': 'bg-blue-100 text-blue-700',
  Açıldı: 'bg-cyan-100 text-cyan-700',
  'Devam Ediyor': 'bg-green-100 text-green-700',
  Tamamlandı: 'bg-slate-100 text-slate-600',
  İptal: 'bg-red-100 text-red-700',
};

const DAYS = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
const STATUSES = ['Talep Toplama', 'Açıldı', 'Devam Ediyor', 'Tamamlandı', 'İptal'];
const LEVELS = ['İlkokul', 'Ortaokul', 'LGS', 'Lise', 'YKS'];

export default function GrupDersleriPage() {
  const [data, setData] = useState<GrupDers[]>([]);
  const [teachers, setTeachers] = useState<{id: string; ad_soyad: string}[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [detailItem, setDetailItem] = useState<GrupDers | null>(null);
  const [editItem, setEditItem] = useState<GrupDers | null>(null);
  const [deleteItem, setDeleteItem] = useState<GrupDers | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [editForm, setEditForm] = useState<{
    name: string; branch: string; level: string; teacherId: string;
    maxCapacity: string; days: string[]; time: string; price: string;
    description: string; status: string; startDate: string;
  } | null>(null);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const openEdit = (g: GrupDers) => {
    setDetailItem(null);
    setEditItem(g);
    setEditForm({
      name: g.grup_adi, branch: g.brans, level: g.seviye,
      teacherId: g.ogretmen_id || '', maxCapacity: String(g.maksimum_kontenjan || 6),
      days: g.ders_gunleri || [], time: g.ders_saati?.slice(0,5) || '',
      price: String(g.grup_ders_ucreti || ''),
      description: g.aciklama || '', status: g.durum || 'Talep Toplama',
      startDate: g.baslangic_tarihi || '',
    });
  };

  const toggleEditDay = (d: string) => setEditForm((p) => p ? ({ ...p, days: p.days.includes(d) ? p.days.filter((x) => x !== d) : [...p.days, d] }) : p);

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editItem || !editForm) return;
    setSaving(true);
    await supabase.from('egtpanel_grup_dersleri').update({
      grup_adi: editForm.name, brans: editForm.branch, seviye: editForm.level,
      ogretmen_id: editForm.teacherId || null, maksimum_kontenjan: Number(editForm.maxCapacity),
      ders_gunleri: editForm.days, ders_saati: editForm.time || null,
      grup_ders_ucreti: Number(editForm.price) || null,
      baslangic_tarihi: editForm.startDate || null,
      aciklama: editForm.description || null, durum: editForm.status,
    }).eq('id', editItem.id);
    setSaving(false);
    setEditItem(null); setEditForm(null);
    showToast('Grup güncellendi!');
    fetchData();
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    setSaving(true);
    await supabase.from('egtpanel_grup_dersleri').delete().eq('id', deleteItem.id);
    setSaving(false);
    setDeleteItem(null);
    showToast('Grup silindi.');
    fetchData();
  };
  const [form, setForm] = useState({
    name: '', branch: '', level: '', teacherId: '', maxCapacity: '6',
    days: [] as string[], time: '', price: '', description: '', status: 'Talep Toplama', startDate: '',
  });

  const fetchData = useCallback(async () => {
    const { data: rows } = await supabase
      .from('egtpanel_grup_dersleri')
      .select('*, ogretmen:egtpanel_ogretmenler(ad_soyad)')
      .order('olusturma_tarihi', { ascending: false });
    if (rows) {
      const withCounts = await Promise.all(rows.map(async r => {
        const { count } = await supabase.from('egtpanel_grup_kayitlari').select('id', { count: 'exact', head: true }).eq('grup_id', r.id);
        return { ...r, kayit_sayisi: count || 0 };
      }));
      setData(withCounts as GrupDers[]);
    }
    const { data: t } = await supabase.from('egtpanel_ogretmenler').select('id, ad_soyad').order('ad_soyad');
    setTeachers(t || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleDay = (d: string) => setForm(p => ({
    ...p, days: p.days.includes(d) ? p.days.filter(x => x !== d) : [...p.days, d]
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    await supabase.from('egtpanel_grup_dersleri').insert({
      grup_adi: form.name,
      brans: form.branch,
      seviye: form.level,
      ogretmen_id: form.teacherId || null,
      maksimum_kontenjan: Number(form.maxCapacity),
      ders_gunleri: form.days,
      ders_saati: form.time || null,
      grup_ders_ucreti: Number(form.price) || null,
      baslangic_tarihi: form.startDate || null,
      aciklama: form.description || null,
      durum: form.status,
    });
    setSaving(false);
    setShowModal(false);
    setForm({ name: '', branch: '', level: '', teacherId: '', maxCapacity: '6', days: [], time: '', price: '', description: '', status: 'Talep Toplama', startDate: '' });
    fetchData();
  };

  if (loading) return (
    <div className="p-6 flex items-center justify-center py-32">
      <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Grup Dersleri</h1>
          <p className="text-slate-500 text-sm mt-1">Grup ders programlarını oluşturun ve yönetin</p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-[#1e3a5f] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#162d4a] whitespace-nowrap cursor-pointer">
          <i className="ri-add-line" /> Grup Oluştur
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Toplam Grup', val: data.length, color: 'text-slate-700', bg: 'bg-slate-50' },
          { label: 'Devam Eden', val: data.filter(g => g.durum === 'Devam Ediyor').length, color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'Talep Toplayan', val: data.filter(g => g.durum === 'Talep Toplama').length, color: 'text-blue-700', bg: 'bg-blue-50' },
          { label: 'Toplam Kayıtlı', val: data.reduce((a, g) => a + (g.kayit_sayisi || 0), 0), color: 'text-indigo-700', bg: 'bg-indigo-50' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
            <p className="text-xs text-slate-500 font-medium">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.val}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {data.length === 0 ? (
          <div className="col-span-3 bg-white rounded-xl border border-slate-100 p-16 text-center">
            <i className="ri-group-line text-5xl text-slate-200 block mb-3" />
            <p className="text-slate-500">Henüz grup dersi oluşturulmamış</p>
          </div>
        ) : data.map(g => {
          const kayit = g.kayit_sayisi || 0;
          const kapasite = g.maksimum_kontenjan || 1;
          const pct = Math.min((kayit / kapasite) * 100, 100);
          return (
            <div key={g.id} className="bg-white rounded-xl border border-slate-100 overflow-hidden hover:border-slate-200 transition-all">
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-slate-800">{g.grup_adi}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-slate-500">{g.brans}</span>
                      <span className="text-slate-300">·</span>
                      <span className="text-xs text-slate-500">{g.seviye}</span>
                    </div>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusColors[g.durum || ''] || 'bg-slate-100 text-slate-500'}`}>{g.durum}</span>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-user-star-line text-slate-400 text-base w-4" />
                    <span className="text-slate-600">{(g.ogretmen as {ad_soyad: string} | undefined)?.ad_soyad || '—'}</span>
                  </div>
                  {g.ders_gunleri && g.ders_gunleri.length > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <i className="ri-calendar-line text-slate-400 text-base w-4" />
                      <span className="text-slate-600">{g.ders_gunleri.join(', ')} {g.ders_saati ? `— ${g.ders_saati.slice(0,5)}` : ''}</span>
                    </div>
                  )}
                  {g.grup_ders_ucreti && (
                    <div className="flex items-center gap-2 text-sm">
                      <i className="ri-money-dollar-circle-line text-slate-400 text-base w-4" />
                      <span className="text-slate-600">₺{Number(g.grup_ders_ucreti).toLocaleString('tr-TR')} / ay</span>
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                    <span>Kontenjan</span>
                    <span className="font-medium">{kayit}/{kapasite}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-red-400' : pct >= 80 ? 'bg-orange-400' : 'bg-green-400'}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setDetailItem(g)} className="flex-1 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors cursor-pointer">Detay</button>
                  <button onClick={() => openEdit(g)} className="px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer"><i className="ri-edit-line text-sm" /></button>
                  <button onClick={() => setDeleteItem(g)} className="px-3 py-2 rounded-lg border border-red-100 text-red-500 hover:bg-red-50 transition-colors cursor-pointer"><i className="ri-delete-bin-line text-sm" /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {toast && <div className="fixed top-4 right-4 z-50 bg-[#1e3a5f] text-white px-5 py-3 rounded-xl text-sm font-medium">{toast}</div>}

      {/* Edit Modal */}
      {editItem && editForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">Grubu Düzenle</h2>
              <button onClick={() => { setEditItem(null); setEditForm(null); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer"><i className="ri-close-line" /></button>
            </div>
            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Grup Adı *</label><input required value={editForm.name} onChange={(e) => setEditForm((p) => p ? ({...p, name: e.target.value}) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Branş</label><select value={editForm.branch} onChange={(e) => setEditForm((p) => p ? ({...p, branch: e.target.value}) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"><option value="">Seçiniz</option>{BRANCHES.map(b => <option key={b}>{b}</option>)}</select></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Seviye</label><select value={editForm.level} onChange={(e) => setEditForm((p) => p ? ({...p, level: e.target.value}) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"><option value="">Seçiniz</option>{LEVELS.map(l => <option key={l}>{l}</option>)}</select></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Öğretmen</label><select value={editForm.teacherId} onChange={(e) => setEditForm((p) => p ? ({...p, teacherId: e.target.value}) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"><option value="">Seçiniz</option>{teachers.map(t => <option key={t.id} value={t.id}>{t.ad_soyad}</option>)}</select></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Maks. Kontenjan</label><input type="number" min="1" max="20" value={editForm.maxCapacity} onChange={(e) => setEditForm((p) => p ? ({...p, maxCapacity: e.target.value}) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" /></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Ders Saati</label><input type="time" value={editForm.time} onChange={(e) => setEditForm((p) => p ? ({...p, time: e.target.value}) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" /></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Grup Ders Ücret (₺)</label><input type="number" value={editForm.price} onChange={(e) => setEditForm((p) => p ? ({...p, price: e.target.value}) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" /></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Başlangıç Tarihi</label><input type="date" value={editForm.startDate} onChange={(e) => setEditForm((p) => p ? ({...p, startDate: e.target.value}) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" /></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Durum</label><select value={editForm.status} onChange={(e) => setEditForm((p) => p ? ({...p, status: e.target.value}) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">{STATUSES.map(s => <option key={s}>{s}</option>)}</select></div>
              </div>
              <div><label className="block text-xs font-medium text-slate-600 mb-2">Ders Günleri</label><div className="flex flex-wrap gap-2">{DAYS.map(d => (<button key={d} type="button" onClick={() => toggleEditDay(d)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${editForm.days.includes(d) ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'text-slate-600 border-slate-200 hover:border-slate-300'}`}>{d}</button>))}</div></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Açıklama</label><textarea value={editForm.description} onChange={(e) => setEditForm((p) => p ? ({...p, description: e.target.value}) : p)} rows={2} maxLength={500} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 resize-none" /></div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setEditItem(null); setEditForm(null); }} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] cursor-pointer">{saving ? <i className="ri-loader-4-line animate-spin" /> : 'Güncelle'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteItem && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-xl mb-4"><i className="ri-delete-bin-line text-red-500 text-xl" /></div>
            <h3 className="font-bold text-slate-800 text-lg mb-1">Grubu Sil</h3>
            <p className="text-slate-500 text-sm mb-6"><strong>{deleteItem.grup_adi}</strong> grubunu silmek istediğinizden emin misiniz?</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteItem(null)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
              <button onClick={handleDelete} disabled={saving} className="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 disabled:opacity-60 cursor-pointer">{saving ? 'Siliniyor...' : 'Evet, Sil'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">Yeni Grup Oluştur</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer"><i className="ri-close-line" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Grup Adı *</label>
                <input required value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="örn: LGS Matematik Grubu A" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Branş</label>
                  <select value={form.branch} onChange={e => setForm(p => ({...p, branch: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option value="">Seçiniz</option>
                    {BRANCHES.map(b => <option key={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Seviye</label>
                  <select value={form.level} onChange={e => setForm(p => ({...p, level: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option value="">Seçiniz</option>
                    {LEVELS.map(l => <option key={l}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Öğretmen</label>
                  <select value={form.teacherId} onChange={e => setForm(p => ({...p, teacherId: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option value="">Seçiniz</option>
                    {teachers.map(t => <option key={t.id} value={t.id}>{t.ad_soyad}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Maks. Kontenjan</label>
                  <input type="number" min="1" max="20" value={form.maxCapacity} onChange={e => setForm(p => ({...p, maxCapacity: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Ders Saati</label>
                  <input type="time" value={form.time} onChange={e => setForm(p => ({...p, time: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Grup Ders Ücreti (₺)</label>
                  <input type="number" value={form.price} onChange={e => setForm(p => ({...p, price: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Başlangıç Tarihi</label>
                  <input type="date" value={form.startDate} onChange={e => setForm(p => ({...p, startDate: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Durum</label>
                  <select value={form.status} onChange={e => setForm(p => ({...p, status: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    {STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">Ders Günleri</label>
                <div className="flex flex-wrap gap-2">
                  {DAYS.map(d => (
                    <button key={d} type="button" onClick={() => toggleDay(d)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${form.days.includes(d) ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'text-slate-600 border-slate-200 hover:border-slate-300'}`}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Açıklama</label>
                <textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} rows={2} maxLength={500} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] cursor-pointer">
                  {saving ? <i className="ri-loader-4-line animate-spin" /> : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailItem && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <div>
                <h2 className="text-lg font-bold text-slate-800">{detailItem.grup_adi}</h2>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[detailItem.durum || ''] || 'bg-slate-100'}`}>{detailItem.durum}</span>
              </div>
              <button onClick={() => setDetailItem(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer"><i className="ri-close-line" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  { label: 'Branş', val: detailItem.brans },
                  { label: 'Seviye', val: detailItem.seviye },
                  { label: 'Öğretmen', val: (detailItem.ogretmen as {ad_soyad: string} | undefined)?.ad_soyad || '—' },
                  { label: 'Günler', val: detailItem.ders_gunleri?.join(', ') || '—' },
                  { label: 'Saat', val: detailItem.ders_saati?.slice(0,5) || '—' },
                  { label: 'Ücret', val: detailItem.grup_ders_ucreti ? `₺${Number(detailItem.grup_ders_ucreti).toLocaleString('tr-TR')}` : '—' },
                  { label: 'Başlangıç', val: detailItem.baslangic_tarihi || '—' },
                  { label: 'Kontenjan', val: `${detailItem.kayit_sayisi || 0}/${detailItem.maksimum_kontenjan || '?'}` },
                ].map(r => (
                  <div key={r.label} className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-400">{r.label}</p>
                    <p className="font-medium text-slate-700 mt-0.5">{r.val}</p>
                  </div>
                ))}
              </div>
              {detailItem.aciklama && <p className="text-sm text-slate-600 bg-blue-50 rounded-xl p-3">{detailItem.aciklama}</p>}
              <button onClick={() => setDetailItem(null)} className="w-full py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">Kapat</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}